import React from "react"
import { Grid, Container, Typography, Hidden, useMediaQuery, useTheme } from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { useAuth } from "@services/useAuth"
import { Icon } from "../Icon/Icon"
import { RequirePermissions } from "../Permissions/RequirePermissions"
import { TimerFloatingSpeedDial } from "../FloatingSpeedDial/TimerFloatingSpeedDial"
import { ColumnBox } from "../Boxes/ColumnBox"
import { RowBox } from "../Boxes/RowBox"
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary"
import OfflineModeBanner from "../Offline/OfflineModeBanner"
import { GlobalNoticesBanner } from "../Notices/GlobalNoticesBanner"

const useStyles = makeStyles((theme) => ({
  area: {
    ...theme.cards.areaCard,
    borderBottom: ({ bottomBorder }) => (bottomBorder ? theme.cards.areaCard.borderBottom : 0),
    background: ({ xs }) => (xs ? theme.palette.background.default : "inherit"),
    height: ({ collapse }) => (collapse ? 0 : "auto"),
    minHeight: "auto",
    display: "flex",
    alignItems: "center"
  },
  grid: {
    paddingTop: ({ xs }) => (xs ? theme.spacing(1) : theme.spacing(3)),
    paddingBottom: ({ xs }) => (xs ? theme.spacing(1) : theme.spacing(3))
  },
  title: {
    fontSize: ({ xs, sm }) => (xs ? 24 : sm ? 22 : 28),
    fontWeight: "700",
    padding: 0
  },
  titleIcon: {
    marginRight: theme.spacing(1)
  },
  titleHasSubtitle: {
    lineHeight: "1.07",
    margin: "3px 0 3px 0"
  },
  beta: {
    fontSize: 12,
    fontWeight: "600",
    opacity: 0.6,
    marginLeft: theme.spacing(0.5)
  },
  subTitle: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: "1.3"
  }
}))

const AreaHeader = ({
  title,
  titleIcon,
  subTitle,
  desktopButtons,
  mobileButtons,
  bottomBorder = true,
  collapse = false,
  beta = false,
  cy = "AreaHeader"
}) => {
  const theme = useTheme()
  const { featureDependencies } = useAuth()

  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const sm = useMediaQuery(theme.breakpoints.only("sm"))
  const classes = useStyles({ collapse, xs, sm, bottomBorder })

  const className = `${classes.area}`

  const titleIconItem = titleIcon ? <Icon name={titleIcon} className={classes.titleIcon} fontSize="large" /> : null

  return (
    <ErrorBoundary>
      <OfflineModeBanner />
      <GlobalNoticesBanner />
      <Grid className={className} data-cy={cy}>
        {!collapse && (
          <Container maxWidth={false}>
            <Grid
              container
              wrap="nowrap"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={classes.grid}
            >
              <Grid item>
                <Grid container spacing={0} align="left" direction="column">
                  <Grid item>
                    <RowBox alignItems="center">
                      {!xs && titleIconItem}
                      <ColumnBox>
                        <RowBox>
                          <Typography
                            variant="h1"
                            className={`${classes.title} ${subTitle ? classes.titleHasSubtitle : ""}`}
                            data-cy={`${cy}-title`}
                          >
                            {title}
                          </Typography>
                          {beta && (
                            <Typography className={classes.beta}>
                              <sup>BETA</sup>
                            </Typography>
                          )}
                        </RowBox>
                        {subTitle && (
                          <Typography component="span" className={classes.subTitle}>
                            {subTitle}
                          </Typography>
                        )}
                      </ColumnBox>
                    </RowBox>
                  </Grid>
                </Grid>
              </Grid>
              {desktopButtons && <Hidden xsDown>{desktopButtons}</Hidden>}
              {mobileButtons && <Hidden smUp>{mobileButtons}</Hidden>}
            </Grid>
          </Container>
        )}
      </Grid>
      <RequirePermissions features={featureDependencies.timers} requires={["timer_read"]}>
        <TimerFloatingSpeedDial />
      </RequirePermissions>
    </ErrorBoundary>
  )
}

export default AreaHeader
